import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  suportaAvisos,
  estadoPermissao,
  ligarAvisosGerais,
  precisaInstalarNoIphone,
} from '@/lib/avisos';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

// ---------------------------------------------------------------------------
// Cartão no topo de /meu-talao: convida o vendedor da ação entre amigos a
// ligar os avisos no celular. Some quando já está ligado.
// No iPhone só funciona com o app na tela inicial; no navegador, orienta a
// instalar. Reusa o mesmo fluxo de assinatura (push_assinar) dos avisos gerais.
// ---------------------------------------------------------------------------
type Estado = 'checando' | 'escondido' | 'pedir' | 'iphone';

export default function AvisosTalao() {
  const navigate = useNavigate();
  const [estado, setEstado] = useState<Estado>('checando');
  const [ligando, setLigando] = useState(false);

  useEffect(() => {
    if (!suportaAvisos()) {
      setEstado('escondido');
      return;
    }
    if (estadoPermissao() === 'granted') {
      setEstado('escondido');
      return;
    }
    if (precisaInstalarNoIphone()) {
      setEstado('iphone');
      return;
    }
    setEstado('pedir');
  }, []);

  async function ativar() {
    setLigando(true);
    try {
      await ligarAvisosGerais();
      setEstado('escondido');
      mostrarToast('Avisos ligados! Você recebe no celular.', 5000, 'sucesso');
    } catch (e) {
      if (estadoPermissao() !== 'default') setEstado('escondido');
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 6000, 'erro');
    } finally {
      setLigando(false);
    }
  }

  if (estado === 'checando' || estado === 'escondido') return null;

  return (
    <div className="app-entra mb-4 rounded-[18px] bg-background-50 p-4 shadow-pcard">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-primary-500 text-white">
          <i className="ri-notification-3-line text-xl" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[16px] font-bold leading-snug text-foreground-950">
            {estado === 'iphone'
              ? 'Instale o app para receber avisos'
              : 'Ligue os avisos para saber quando venderem seus números'}
          </p>
          <p className="mt-1 text-[14px] leading-relaxed text-foreground-700">
            {estado === 'iphone'
              ? 'No iPhone, os avisos só funcionam com o Se Liga na tela de início. Toque em “Como instalar” e siga os 3 passos.'
              : 'Assim você recebe na hora cada número vendido, sem precisar ficar abrindo o talão.'}
          </p>
        </div>
      </div>

      {estado === 'iphone' ? (
        <button
          type="button"
          onClick={() => navigate('/instalar')}
          className="aperta mt-4 flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-download-2-line" aria-hidden />
          </span>
          Como instalar
        </button>
      ) : (
        <button
          type="button"
          onClick={ativar}
          disabled={ligando}
          className="aperta mt-4 flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className={ligando ? 'ri-loader-4-line animate-spin' : 'ri-notification-3-line'} aria-hidden />
          </span>
          {ligando ? 'Ligando…' : 'Ativar avisos'}
        </button>
      )}
    </div>
  );
}